import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsAwsResourcesService } from "./finops-aws-resources.service";
import {
    LoadFinOpsAwsResourcesHistoricalData,
    SetFinOpsAwsResource,
    ClearFinOpsAwsResource,
    LoadFinOpsAwsResourcesChartsData,
    SelectFinOpsDetailsInstance,
    LoadDataTableAllAccountsFinOpsAwsResources,
    LoadFinOpsAwsResourcesAllAccountsChartsFilteredData
} from "./finops-aws-resources.actions";
import { finalize, forkJoin, Observable, tap } from "rxjs";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { FinOpsAwsDetailsEc2Instance, FinOpsAwsDetailsRdsInstance, FinOpsAwsDetailsResource, FinOpsAwsResourcesDayMeasures, FinOpsAwsResourcesStateModel } from "./finops-aws-resources.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { AwsResourceTypeLabelPipe } from "src/app/shared/pipe/aws-resource-type-label.pipe";
import { TranslateService } from "@ngx-translate/core";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { DataForDatatableCompute, FinOpsAwsResourcesHelper } from "./finops-aws-resources.helper";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { AwsConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

export function isRdsInstance(
    value: FinOpsAwsDetailsResource | FinOpsAwsDetailsRdsInstance | FinOpsAwsDetailsEc2Instance    
): value is FinOpsAwsDetailsRdsInstance {
    return (
        value != null &&
        (value as FinOpsAwsDetailsRdsInstance).engine !== undefined &&
        (value as FinOpsAwsDetailsRdsInstance).engine_version !== undefined
    )
}

export function isEc2Instance(
    value: FinOpsAwsDetailsResource | FinOpsAwsDetailsRdsInstance | FinOpsAwsDetailsEc2Instance    
): value is FinOpsAwsDetailsEc2Instance {
    return (
        value != null &&
        (value as FinOpsAwsDetailsEc2Instance).autoscaling !== undefined &&
        (value as FinOpsAwsDetailsEc2Instance).image_id !== undefined
    )
}

const FINOPS_AWS_RESOURCES_TOKEN: StateToken<FinOpsAwsResourcesStateModel> = new StateToken('finOpsAwsResources');

const FinOpsAwsResourcesStateModelDefaults: FinOpsAwsResourcesStateModel = {
    historicalDataByAccount: {},
    datatable: [],
    chartsDataByAccount: {},
    chartsDataDisplayed: {
        filteredHistoricalChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            costs: []
        },
        filteredCompareProjectsChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            dateString: "",
            prediction: false,
            costs: []
        },
        filteredCompareTypesChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            dateString: "",
            prediction: false,
            costs: []
        },
    },
    filteredCounters: {},
    detailsData: {
        account: '',
        date: '',
        resource: {
            account: '',
            type: '',
            name: '',
            age: 0,
            region: '',
            tags: Object,
            state: 'compliant',
            projects: [],
            issues: [],
            month_costs: {},
            ec2_volume_attachments: []
        },
        ec2_instances: [],
        rds_instances: []
    },
    detailsSelectedInstance: {
        account: '',
        type: '',
        name: '',
        age: 0,
        region: '',
        tags: Object,
        state: 'compliant',
        projects: [],
        issues: [],
        month_costs: {},
        ec2_volume_attachments: []
    }
}

@State<FinOpsAwsResourcesStateModel>({
    name: FINOPS_AWS_RESOURCES_TOKEN,
    defaults: FinOpsAwsResourcesStateModelDefaults
})

@Injectable()
export class FinOpsAwsResourcesState {

    private readonly KEY = FINOPS_AWS_RESOURCES_TOKEN.getName()

    constructor(
        private finOpsAwsResourcesService: FinOpsAwsResourcesService,
        private store: Store,
        private awsResourceTypeLabelPipe: AwsResourceTypeLabelPipe,
        private floorPipe: FloorPipe,
        private numberFormatPipe: FormatNumberPipe,
        private dateService: DateService,
        private translate: TranslateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getHistoricalDataByAccountByGranularityByDate(state: FinOpsAwsResourcesStateModel) {
        return (account: string, granularity: string, date: string) => state.historicalDataByAccount[account][granularity][date]
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getDatatable(state: FinOpsAwsResourcesStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFilteredCounters(state: FinOpsAwsResourcesStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getChartsDataByAccountAndDate(state: FinOpsAwsResourcesStateModel) {
        return (account: string, date:string) => state.chartsDataByAccount[account][date]
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFilteredHistoricalChartData(state: FinOpsAwsResourcesStateModel) {
        return state.chartsDataDisplayed.filteredHistoricalChartData
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFilteredCompareProjectsChartData(state: FinOpsAwsResourcesStateModel) {
        return state.chartsDataDisplayed.filteredCompareProjectsChartData
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFilteredCompareTypesChartData(state: FinOpsAwsResourcesStateModel) {
        return state.chartsDataDisplayed.filteredCompareTypesChartData
    }
    
    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static listFinOpsDetailsEc2Instances(state: FinOpsAwsResourcesStateModel) {
        if(state.detailsData.ec2_instances.length > 0) {
            return state.detailsData.ec2_instances
        }
        return []
    }
    
    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static listFinOpsDetailsRdsInstances(state: FinOpsAwsResourcesStateModel) {
        if(state.detailsData.rds_instances.length > 0) {
            return state.detailsData.rds_instances
        }
        return []
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFinOpsDetailsDate(state: FinOpsAwsResourcesStateModel) {
        return state.detailsData.date
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFinOpsDetailsResource(state: FinOpsAwsResourcesStateModel) {
        return state.detailsData.resource
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFinOpsDetailsSelectedResource(state: FinOpsAwsResourcesStateModel) {
        if (state.detailsSelectedInstance.name != '') {
            return state.detailsSelectedInstance
        }
        return state.detailsData.resource
    }

    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFinOpsDetailsSelectedInstance(state: FinOpsAwsResourcesStateModel) {
        return state.detailsSelectedInstance
    }
    
    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFinOpsDetailsSelectedRdsInstance(state: FinOpsAwsResourcesStateModel): FinOpsAwsDetailsRdsInstance | null  {
        if (state.detailsSelectedInstance && isRdsInstance(state.detailsSelectedInstance)) {
            return state.detailsSelectedInstance
        }
        return null
    }
    
    @Selector([FINOPS_AWS_RESOURCES_TOKEN])
    static getFinOpsDetailsSelectedEc2Instance(state: FinOpsAwsResourcesStateModel): FinOpsAwsDetailsEc2Instance | null  {
        if (state.detailsSelectedInstance && isEc2Instance(state.detailsSelectedInstance)) {
            return state.detailsSelectedInstance
        }
        return null
    }

    @Action(LoadFinOpsAwsResourcesHistoricalData)
    loadFinOpsAwsResourcesHistoricalData(ctx: StateContext<FinOpsAwsResourcesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const historicalDates: string[] = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
        let wrapRequestList = []
        let accounts: string[] = []
        awsConfig.accounts.forEach(account => {
            accounts.push(account.account_id)
        });

        for(const account of accounts) {
            for (const historalDate of historicalDates) {
                const FINOPS_RESOURCE_DATE_KEY = this.KEY
                + '-account-'
                + account
                + "-granularity-"
                + finopsConfig.granularity
                + "-date-"
                + historalDate

                if (!this.metaService.isStale(FINOPS_RESOURCE_DATE_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_RESOURCE_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }

                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_RESOURCE_DATE_KEY,
                        this.finOpsAwsResourcesService.loadFinOpsAwsResourcesHistoricalData(historalDate, finopsConfig.granularity, account),
                        resources => {
                            ctx.setState(state => {
                                const h = {...state.historicalDataByAccount};
                                h[account] = { ...(h[account] || {} )};
                                h[account][finopsConfig.granularity] = {
                                    ...(h[account][finopsConfig.granularity] || {}),
                                    [historalDate]: resources.measures
                                };
                                return {
                                    ...state,
                                    historicalDataByAccount: h
                                }
                            })
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(new LoadDataTableAllAccountsFinOpsAwsResources())
            })
        )
    }

    @Action(LoadDataTableAllAccountsFinOpsAwsResources)
    loadDataTableAllAccountsFinOpsAwsResources(ctx: StateContext<FinOpsAwsResourcesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const granularity = finopsConfig.granularity as DateGranularityEnum
        const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_RESOURCES]]
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)

        // If cannot find selected date in historical dates, select the last one and load data for it
        if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
            ctx.dispatch([
                new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                new LoadDataTableAllAccountsFinOpsAwsResources()
            ])
            return
        }

        if (isDevMode()) {
            console.warn("Loading all accounts data for granularity " + granularity + " and date " + selectedDateTile)
        }

        let total: Record<string, DataForDatatableCompute> = {}
        let grandTotal: DataForDatatableCompute = {
            filteredCounters: {},
            measuresDatatable: {},
            previousTotalCounters: {}            
        }

        for(const account of awsConfig.accounts) {
            const historicalDataByGranularity = ctx.getState().historicalDataByAccount[account.account_id][granularity]
            const dataForDatatable = FinOpsAwsResourcesHelper.computeDataTable(historicalDatesSelected, historicalDataByGranularity, filters)
            total = {
                ...total,
                [account.account_id]: dataForDatatable
            }
        }

        grandTotal = FinOpsAwsResourcesHelper.computeDatatableGrandTotal(total)

        ctx.patchState(
            {
                datatable: grandTotal.measuresDatatable[selectedDateTile],
                filteredCounters: grandTotal.filteredCounters
            }
        )
    }

    @Action(ClearFinOpsAwsResource)
    clearFinOpsAwsResource(ctx: StateContext<FinOpsAwsResourcesStateModel>) {
        return ctx.patchState({
            detailsData: FinOpsAwsResourcesStateModelDefaults.detailsData,
            detailsSelectedInstance: FinOpsAwsResourcesStateModelDefaults.detailsSelectedInstance
        })
    }

    @Action(SetFinOpsAwsResource)
    setFinOpsAwsResource(ctx: StateContext<FinOpsAwsResourcesStateModel>, {accountId, resourceId, selectedDate}: SetFinOpsAwsResource) {
        // If no selected date, retrieve from config
        let selectedDateStr = ""
        if (selectedDate === undefined) {
            selectedDateStr = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
        } else {
            selectedDateStr = this.dateService.formatShortDate(selectedDate)
        }
        return this.finOpsAwsResourcesService.loadFinOpsAwsResourceDetails(
            selectedDateStr,
            accountId!,
            resourceId
        ).pipe(
            tap(value => {
                let detailsSelectedInstance = FinOpsAwsResourcesStateModelDefaults.detailsSelectedInstance
                // If only 1 EC2 Instance, select it
                if(value.ec2_instances.length == 1) {
                    detailsSelectedInstance = value.ec2_instances[0]
                }
                // If only 1 RDS Instance, select it
                if(value.rds_instances.length > 0) {
                    detailsSelectedInstance = value.rds_instances[0]
                }
                return ctx.patchState({
                    detailsData: value,
                    detailsSelectedInstance: detailsSelectedInstance
                })
            }),
        )
    }

    @Action(SelectFinOpsDetailsInstance)
    selectFinOpsDetailsInstance(ctx: StateContext<FinOpsAwsResourcesStateModel>, payload: any) {
        let detailsData = ctx.getState().detailsData
        let detailsSelectedInstance = FinOpsAwsResourcesStateModelDefaults.detailsSelectedInstance
        detailsData.ec2_instances.forEach(instance => {
            if (instance.instance_id == payload.payload) {
                detailsSelectedInstance = instance
            }
        })
        detailsData.rds_instances.forEach(instance => {
            if (instance.instance_id == payload.payload) {
                detailsSelectedInstance = instance
            }
        })
        ctx.patchState({
            detailsSelectedInstance: detailsSelectedInstance
        })
    }

    @Action(LoadFinOpsAwsResourcesChartsData)
    loadFinOpsAwsResourcesChartsData(ctx: StateContext<FinOpsAwsResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getChartsHistoricalDates(chartsConfig)
        let wrapRequestList: Observable<FinOpsAwsResourcesDayMeasures>[] = []
        let accounts: string[] = []

        awsConfig.accounts.forEach(account => {
            accounts.push(account.account_id)
        });
        
        for(const _account of accounts) {
            for (const historicalDate of historicalDates.map(item => item.date)) {
                const FINOPS_RESOURCE_CHARTS_DATE_KEY = this.KEY
                + "-charts-"
                + "account-" + _account
                + "-date-" + historicalDate

                if(!this.metaService.isStale(FINOPS_RESOURCE_CHARTS_DATE_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_RESOURCE_CHARTS_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }

                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_RESOURCE_CHARTS_DATE_KEY,
                        this.finOpsAwsResourcesService.loadFinOpsAwsResourcesMeasures(historicalDate, _account),
                        (chart_resources) => {
                            ctx.setState(state => {
                                const shallowCloneAllChartsData = {...state.chartsDataByAccount}
                                shallowCloneAllChartsData[_account] = {...(shallowCloneAllChartsData[_account] || {})}
                                shallowCloneAllChartsData[_account][historicalDate] = chart_resources.measures
                                return {
                                    ...state,
                                    chartsDataByAccount: shallowCloneAllChartsData
                                }                                
                            })
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(
                    new LoadFinOpsAwsResourcesAllAccountsChartsFilteredData()
                )
            })
        )
    }

    @Action(LoadFinOpsAwsResourcesAllAccountsChartsFilteredData)
    loadFinOpsAwsResourcesAllAccountsChartsFilteredData(ctx: StateContext<FinOpsAwsResourcesStateModel>){
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)

        // All chartsData for all accounts and for all retrieved dates.
        const bulkChartsData = ctx.getState().chartsDataByAccount

        // Retrieve granularity 
        const granularity = chartsConfig.granularity

        // Retrieve filters to adjust costs depending on selected filters
        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_RESOURCES_CHARTS]]

        // Get all date to retrieve data from
        const historicalDatesSelected = this.dateService.getChartsHistoricalDates(chartsConfig).map(item => item.date)

        // Get date for compare Chart
        const computeChartDataLastDate = historicalDatesSelected[historicalDatesSelected.length -1]


        const aggregatedHistoricalData = FinOpsAwsResourcesHelper.computeAggregatedHistoricalChartData(
            awsConfig,
            historicalDatesSelected,
            bulkChartsData,
            filters,
            granularity,
            this.floorPipe, this.translate, this.dateService
        )

        const aggregatedCompareTypesData = FinOpsAwsResourcesHelper.computeAggredatedCompareTypesChartData(
            awsConfig,
            bulkChartsData,
            computeChartDataLastDate,
            granularity,
            filters,
            this.floorPipe, this.numberFormatPipe, this.dateService, this.awsResourceTypeLabelPipe
        )

        const aggregatedCompareProjectsData = FinOpsAwsResourcesHelper.computeAggregatedCompareProjectsChartData(
            bulkChartsData,
            computeChartDataLastDate,
            granularity,
            filters,
            this.floorPipe,
            this.numberFormatPipe,
            this.dateService
            );

        ctx.patchState({
            chartsDataDisplayed: {
                filteredHistoricalChartData: aggregatedHistoricalData,
                filteredCompareTypesChartData: aggregatedCompareTypesData,
                filteredCompareProjectsChartData: aggregatedCompareProjectsData
            }
        })
    }

}
